import React, { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Divider as MuiDivider,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Autocomplete,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { Box, spacing } from "@mui/system";
import {
  fetchCoa,
  createAccount,
  updateAccount,
  deleteAccount,
} from "../../../redux/slices/accounting";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import useAuth from "../../../hooks/useAuth";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

function CoaList(props: {
  accounts: any;
  isReady: any;
  onAddAccount: () => void;
  onEditAccount: (account: any) => void;
  onDeleteAccount: (account: any) => void;
}) {
  const { accounts, onAddAccount, onEditAccount, onDeleteAccount } = props;

  const combinedRows = [
    ...(accounts.incomeStatement || []),
    ...(accounts.assets || []),
    ...(accounts.liabilities || []),
  ];

  const tree = buildTree(combinedRows);

  return (
    <Card mb={6}>
      <CardContent
        pb={1}
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Tilikartta
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<AddIcon />}
          onClick={onAddAccount}
        >
          Lisää tili
        </Button>
      </CardContent>

      <Paper>
        <Table>
          <TableBody>
            {!props.isReady ? (
              <TableRow>
                <TableCell align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50vh"
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              tree.flatMap((node) =>
                renderCoaNode(node, 0, onEditAccount, onDeleteAccount)
              )
            )}
          </TableBody>
        </Table>
      </Paper>
    </Card>
  );
}

function renderCoaNode(
  node: any,
  level = 0,
  onEditAccount?: (account: any) => void,
  onDeleteAccount?: (account: any) => void
): JSX.Element[] {
  const paddingLeft = level * 20;

  return [
    <TableRow
      key={node.id}
      sx={{
        backgroundColor: level === 0 ? "rgba(0, 0, 0, 0.02)" : "transparent",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.01)",
        },
      }}
    >
      <TableCell
        style={{
          paddingLeft,
          fontWeight: 600,
          fontSize: level === 0 ? "0.95rem" : "0.875rem",
          paddingTop: level === 0 ? 16 : 12,
          paddingBottom: level === 0 ? 16 : 12,
        }}
      >
        {node.name}
      </TableCell>
    </TableRow>,
    ...(node.accounts || []).map((acc: any) => (
      <TableRow
        key={acc.code}
        sx={{
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.02)",
          },
          transition: "background-color 0.2s",
        }}
      >
        <TableCell
          style={{
            paddingLeft: paddingLeft + 20,
            paddingTop: 10,
            paddingBottom: 10,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Box
                component="span"
                sx={{ fontWeight: 500, color: "text.secondary", mr: 1 }}
              >
                {acc.code}
              </Box>
              <Box component="span">{acc.name}</Box>
            </Box>
            {acc.id && (
              <Box>
                <IconButton
                  size="small"
                  onClick={() => onEditAccount?.(acc)}
                  sx={{ mr: 0.5 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onDeleteAccount?.(acc)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        </TableCell>
      </TableRow>
    )),
    ...(node.children || []).flatMap((child: any) =>
      renderCoaNode(child, level + 1, onEditAccount, onDeleteAccount)
    ),
  ];
}

function buildTree(data: any[]): any[] {
  const map = new Map();
  const roots: any[] = [];

  for (const node of data) {
    map.set(node.id, { ...node, children: [] });
  }

  for (const node of data) {
    if (node.parentId) {
      const parent = map.get(node.parentId);
      if (parent) {
        parent.children.push(map.get(node.id));
      }
    } else {
      roots.push(map.get(node.id));
    }
  }

  return roots.sort((a, b) => a.order - b.order);
}

function Coa() {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const { accounts, status: accountingStatus } = useAppSelector(
    (state) => state.accounting
  );
  const { status: propertyStatus } = useAppSelector((state) => state.property);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState<any>(null);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    rowId: "",
  });

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const isReady =
    accountingStatus === "idle" && propertyStatus === "idle" && accounts;

  useEffect(() => {
    if (associationId) {
      dispatch(fetchCoa(associationId));
    }
  }, [dispatch, associationId]);

  // Selectable statement groups for the account dialog: the leaf rows of the
  // /coa tree (only leaves carry accounts in reports), labelled with their
  // Finnish parent path instead of the raw rowId.
  type RowOption = {
    rowId: string;
    label: string;
    section: string;
    codes: number[];
  };
  const rowOptions: RowOption[] = useMemo(() => {
    if (!accounts?.assets) return [];
    const sections = [
      { label: "Tase — Vastaavaa", rows: accounts.assets },
      { label: "Tase — Vastattavaa", rows: accounts.liabilities },
      { label: "Tuloslaskelma", rows: accounts.incomeStatement },
    ];
    const options: RowOption[] = [];
    for (const section of sections) {
      const byId = new Map((section.rows ?? []).map((r: any) => [r.id, r]));
      for (const row of section.rows ?? []) {
        if (!("accounts" in row)) continue; // parent rows carry no accounts
        const crumbs: string[] = [];
        let cur: any = byId.get(row.parentId);
        while (cur && cur.parentId != null) {
          crumbs.unshift(cur.name);
          cur = byId.get(cur.parentId);
        }
        options.push({
          rowId: row.id,
          label: [...crumbs, row.name].join(" → "),
          section: section.label,
          codes: (row.accounts ?? []).map((a: any) => a.code),
        });
      }
    }
    return options;
  }, [accounts]);

  const allCodes = useMemo(
    () => new Set(rowOptions.flatMap((o) => o.codes)),
    [rowOptions]
  );

  // Next free code within the chosen group: one above the group's highest
  // code, bumped past any code already used elsewhere in the chart.
  const suggestCode = (option: RowOption | null): string => {
    if (!option || option.codes.length === 0) return "";
    let candidate = Math.max(...option.codes) + 1;
    while (allCodes.has(candidate)) candidate += 1;
    return String(candidate);
  };

  const selectedRowOption =
    rowOptions.find((o) => o.rowId === formData.rowId) ?? null;

  const codeNumber = formData.code === "" ? null : Number(formData.code);
  const codeTaken =
    codeNumber != null &&
    allCodes.has(codeNumber) &&
    (!editingAccount || codeNumber !== editingAccount.code);

  const handleGroupChange = (option: RowOption | null) => {
    setFormData((prev) => ({
      ...prev,
      rowId: option?.rowId ?? "",
      // Creating: always refresh the suggestion; editing: keep the code.
      code: editingAccount ? prev.code : suggestCode(option),
    }));
  };

  const handleOpenDialog = () => {
    setEditingAccount(null);
    setFormData({ code: "", name: "", rowId: "" });
    setOpenDialog(true);
  };

  const handleEditAccount = (account: any) => {
    setEditingAccount(account);
    setFormData({
      code: account.code.toString(),
      name: account.name,
      rowId: account.rowId,
    });
    setOpenDialog(true);
  };

  const handleDeleteAccount = async (account: any) => {
    if (!associationId) return;

    if (
      window.confirm(
        `Haluatko varmasti poistaa tilin ${account.code} - ${account.name}?`
      )
    ) {
      await dispatch(
        deleteAccount({
          id: account.id,
          associationId,
        })
      );
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAccount(null);
    setFormData({ code: "", name: "", rowId: "" });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!associationId || !formData.code || !formData.name || !formData.rowId) {
      return;
    }

    if (editingAccount) {
      // Update existing account
      await dispatch(
        updateAccount({
          id: editingAccount.id,
          account: {
            code: parseInt(formData.code),
            name: formData.name,
            rowId: formData.rowId,
          },
          associationId,
        })
      );
    } else {
      // Create new account
      await dispatch(
        createAccount({
          account: {
            code: parseInt(formData.code),
            name: formData.name,
            rowId: formData.rowId,
          },
          associationId,
        })
      );
    }

    handleCloseDialog();
  };

  if (!accounts) return null;

  return (
    <React.Fragment>
      <Helmet title="Tilikartta" />
      <Typography variant="h3" gutterBottom display="inline">
        Tilikartta
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Link component={NavLink} to="/">
          Talous
        </Link>
        <Typography>Tilikartta</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <CoaList
        accounts={accounts}
        isReady={isReady}
        onAddAccount={handleOpenDialog}
        onEditAccount={handleEditAccount}
        onDeleteAccount={handleDeleteAccount}
      />

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingAccount ? "Muokkaa tiliä" : "Lisää uusi tili"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Tilin nimi"
                fullWidth
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                required
                autoFocus
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={rowOptions}
                groupBy={(option) => option.section}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(o, v) => o.rowId === v.rowId}
                value={selectedRowOption}
                onChange={(_, option) => handleGroupChange(option)}
                renderOption={(props, option) => (
                  <li {...props} key={option.rowId}>
                    <Box>
                      {option.label}{" "}
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        {option.rowId}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ryhmä"
                    required
                    helperText="Mihin tuloslaskelman tai taseen ryhmään tili kuuluu"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Tilin numero"
                type="number"
                fullWidth
                value={formData.code}
                onChange={(e) => handleInputChange("code", e.target.value)}
                required
                error={codeTaken}
                helperText={
                  codeTaken
                    ? "Tilinumero on jo käytössä"
                    : "Ehdotettu automaattisesti valitun ryhmän mukaan"
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Peruuta
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={
              !formData.code || !formData.name || !formData.rowId || codeTaken
            }
          >
            Tallenna
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}

export default Coa;
